import {
    Admin,
    DataProvider,
    fetchUtils,
    GetListParams,
    GetManyParams,
    GetOneParams,
    List,
    RaRecord,
    Resource,
    useDelete,
    useListController,
} from "react-admin";
import jsonServerProvider from "ra-data-json-server";
import {BASE_URL, IS_SMARTVENUES} from "../../../utils/Utils";
import {Chip, createTheme, ThemeProvider, Tooltip, Typography} from "@mui/material";
import {CustomLayout} from "../../molecules/CustomNameField/CustomNameField";
import {Box, Button, Container, HStack, Image, Spinner, Text, useDisclosure, VStack} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DataGrid} from "@mui/x-data-grid";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import {OrderShow} from "./OrderShow";
import {CustomModal} from "../../molecules/ModalComponent/ModalComponent";
import {ColorsClass} from "../../../theme/colors";
import {LotModelNew} from "../../../data/model/Lot";
import {itIT} from '@mui/x-data-grid/locales';
import {itIT as coreItIT} from '@mui/material/locale';
import {useOrderStore} from "../../../store/use-order-store";
import {useDialogStore} from "../../../store/useAlertDialog";
import {useOrdersHook} from "../../../hooks/useOrdersHook";
import ShippingEdit, {UpdateShippingForm} from "../BillingEdit/BillingEdit";
import {EditIcon} from "@chakra-ui/icons";
import {useModalStore} from "../../../store/useModal";
import polyglotI18nProvider from "ra-i18n-polyglot";
import {EmptyPage} from "../EmptyPage/EmptyPage";
import {useNavigate} from "react-router-dom";

const italianMessages = require("ra-language-italian");
const i18nProvider = polyglotI18nProvider(() => italianMessages, "it");

export const adminHttpClient = (
    url: string,
    options: fetchUtils.Options = {}
) => {
    if (!options.headers) {
        options.headers = new Headers({Accept: "application/json"});
    }
    const token = localStorage.getItem("tokenNew");
    if (token) {
        (options.headers as Headers).set("Authorization", `Bearer ${token}`);
        (options.headers as Headers).set("X-Tenant", IS_SMARTVENUES ? "smartvenues" : "enoteca");
    }
    return fetchUtils.fetchJson(url, options);
};

const dataProvider = jsonServerProvider(BASE_URL, adminHttpClient);

const customDataProvider: DataProvider = {
    ...dataProvider,
    getList: async (resource: string, params: GetListParams) => {
        const {page, perPage} = params.pagination || {};
        const query = {
            page: page || 1,
            limit: perPage || 10,
            sort: params?.sort?.field,
            order: params?.sort?.order,
            ...params.filter,
        };

        const url = `${BASE_URL}/${resource}?${fetchUtils.queryParameters(query)}`;

        const {json, headers} = await adminHttpClient(url);

        let data = [];
        let total = 0;

        if (Array.isArray(json)) {
            data = json.map((item: any) => ({
                id: item.orderId || item.id,
                ...item,
            }));
            const contentRange = headers.get("Content-Range");
            if (contentRange) {
                total = parseInt(contentRange.split("/")[1], 10);
            } else {
                total = data.length;
            }
        } else if (json.data && Array.isArray(json.data)) {
            data = json.data.map((item_1: any) => ({
                id: item_1.orderId || item_1.id,
                ...item_1,
            }));
            total = json.total || data.length;
        }

        return {data, total};
    },
    getOne: async (resource: string, params: GetOneParams) => {
        const url = `${BASE_URL}/${resource}/${params.id}`;
        const {json} = await adminHttpClient(url);
        const data = {
            id: json.orderId || json.id,
            ...json,
        };
        return {data};
    },

    getMany: async (resource: string, params: GetManyParams) => {
        const query = {
            filter: JSON.stringify({id: params.ids}),
        };

        const url = `${BASE_URL}/${resource}?${fetchUtils.queryParameters(query)}`;
        const {json} = await adminHttpClient(url);
        const dataList = Array.isArray(json) ? json : json.data;
        const mappedData = dataList.map((item: any) => ({
            id: item.orderId || item.id,
            ...item,
        }));
        return {data: mappedData};
    }
};

const theme = createTheme({
    palette: {
        background: {
            default: "#f5fbf5"
        }
    },
    components: {
        MuiToolbar: {
            styleOverrides: {
                root: {
                    display: 'none'
                }
            }
        }
    },
}, itIT, coreItIT);

const themeGrid = createTheme({
    palette: {
        background: {
            default: "#f5fbf5"
        }
    },
    components: {
        MuiToolbar: {
            styleOverrides: {
                root: {
                    display: 'flex !important'
                }
            }
        }
    },
}, itIT, coreItIT);

export interface OrderRecord extends RaRecord {
    id: string;
    user?: User;
    quantity?: number;
    subTotal?: number;
    total?: number;
    shippingCharge?: number;
    cancelOrder?: boolean;
    status?: string;
    createdAt?: number | string;
    statusCode?: number;
    shipping?: Shipping;
    items?: Item[];
    prediction?: string;
}

export interface Item {
    id?: string;
    product?: LotModelNew;
    quantity?: number;
}

export interface Category {
    id?: string;
    name?: string;
    description?: string;
    image?: MediaList;
}

export interface MediaList {
    id?: string;
    url?: string;
    mediaType?: string;
    isMain?: boolean;
    name?: string;
    userId?: string;
}

export interface Shipping {
    id?: string;
    userId?: string;
    orderId?: string;
    shipAddress?: string;
    shipCity?: string;
    shipPhone?: string;
    shipName?: string;
    shipEmail?: string;
    shipCountry?: string;
    billingAddress?: string;
    billingCity?: string;
    billingPhone?: string;
    billingName?: string;
    billingCountry?: string;
    invoiceRequired?: boolean;
    piva?: string;
}

export interface User {
    id?: string;
    email?: string;
    mobileNumber?: string;
    isVerified?: boolean;
    userType?: string;
}

/*
PENDING: BG: #e5e5e5 TEXT: #777777
ON HOLD: BG: #f8dda7 TEXT: #94660c
PROCESSING: BG: #c6e1c6 TEXT: #5b841b
COMPLETED: BG: #c8d7e TEXT: #2e4453
 */

function getChipColor(status: string) {
    switch (status) {
        case "PENDING": // In attesa
            return {bg: "#e5e5e5", text: "#777777", name: "In attesa"};
        case "ON_HOLD": // Sospeso
            return {bg: "#d7b8e8", text: "#5a1658", name: "Sospeso"};
        case "PROCESSING": // In lavorazione
            return {bg: "rgba(255,241,204,0.82)", text: "#835e01", name: "In lavorazione"};
        case "COMPLETED": // Completato
            return {bg: "#c8e6c9", text: "#256029", name: "Spedito"};
        case "CONFIRMED": // Confermato
            return {bg: "#acc2ff", text: "#002d9e", name: "Confermato"};
        case "PAID": // Pagato
            return {bg: "#a5d6a7", text: "#064f0a", name: "Pagato"};
        case "DELIVERED": // Consegnato
            return {bg: "#f5d76e", text: "#a57c00", name: "Consegnato"};
        case "CANCELED": // Annullato
            return {bg: "#f5b5b5", text: "#8f0101", name: "Annullato"};
        case "NOT_PAID": // Non Pagato
            return {bg: "#f5b5b5", text: "#8f0101", name: "Attesa pagamento"};
        case "RECEIVED": // Ricevuto
            return {bg: "#d7c3a5", text: "#553504", name: "Ricevuto"};
    }
}

/*
function getChipColor(status: string) {
    switch (status) {
        case "PENDING": // In attesa
            return {bg: "#e5e5e5", text: "#777777", name: "In attesa"};
        case "ON_HOLD": // Sospeso
            return {bg: "#d7b8e8", text: "#5a1658", name: "Sospeso"};
        case "PROCESSING": // In lavorazione
            return {bg: "rgba(255,241,204,0.82)", text: "#835e01", name: "In lavorazione"};
        case "COMPLETED": // Completato
            return {bg: "#c8e6c9", text: "#256029", name: "Spedito"};
        case "CONFIRMED": // Confermato
            return {bg: "#acc2ff", text: "#002d9e", name: "Confermato"};
        case "PAID": // Pagato
            return {bg: "#a5d6a7", text: "#064f0a", name: "Pagato"};
        case "DELIVERED": // Consegnato
            return {bg: "#f5d76e", text: "#a57c00", name: "Consegnato"};
        case "CANCELED": // Annullato
            return {bg: "#f5b5b5", text: "#8f0101", name: "Annullato"};
        case "RECEIVED": // Ricevuto
            return {bg: "#d7c3a5", text: "#553504", name: "Ricevuto"};
    }
}
*/

const OrderListCustom = (props: any, onOpenShow: VoidFunction, changeCurrentOrder: React.Dispatch<React.SetStateAction<OrderRecord | undefined>>) => {
    const controllerProps = useListController(props);
    const {data: controllerData, isLoading} = controllerProps;
    const data: OrderRecord | undefined = controllerData as (OrderRecord | undefined);
    const [deleteOne] = useDelete();

    const {showDialog, closeDialog} = useDialogStore();

    const gridRef = useRef<HTMLDivElement>(null);
    const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});

    const {searchText} = useOrderStore();

    const filteredData = useMemo(() => {
        if (!data) return [];
        return data.filter((order: OrderRecord) =>
            order.shipping?.shipName?.toLowerCase().includes(searchText.toLowerCase()) ||
            order.id?.toString().includes(searchText)
        );
    }, [data, searchText]);

    const columns = [
        {field: 'id', headerName: 'ID', flex: 1},
        {field: 'clientName', headerName: 'Cliente', flex: 1},
        {
            field: 'buyDate',
            headerName: 'Data ordine',
            flex: 1,
            renderCell: (params: any) => new Date(params.value * 1000).toLocaleDateString()
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params: any) => {
                const {bg, text, name} = getChipColor(params?.value?.toUpperCase()) || {};
                return <p style={{fontWeight: 'bold', color: text}}>{name}</p>
                // return <Chip
                //     label={name}
                //     variant="outlined"
                //     sx={{
                //         fontWeight: 'bold',
                //         backgroundColor: bg,
                //         color: text,
                //         width: '100%',
                //     }}
                // />
            },
        },
        {
            field: 'total',
            headerName: 'Prezzo',
            flex: 1,
            renderCell: (params: any) => `${Number(params.value ?? 0)?.toFixed(2)}€`
        },
        {
            field: 'actions',
            headerName: 'Azioni',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: any) => {
                return <div>
                    {/*<IconButton onClick={() => navigate(`#/orders/${params.row.id}/show`)} aria-label={""}>*/}
                    <IconButton onClick={() => {
                        onOpenShow();
                        changeCurrentOrder(params.row.order);
                    }} aria-label={"Show"}>
                        <VisibilityIcon color="primary"/>
                    </IconButton>
                    <IconButton onClick={() => showDialog({
                        title: "Elimina ordine",
                        description: "Sicuro di volere procedere con l'eliminazione?",
                        cancelButtonText: "Annulla",
                        confirmButtonText: "Elimina",
                        onPressCancelButton: () => closeDialog(),
                        onConfirmButtonClick: () => {
                            deleteOne('orders', {id: params.row.id}).then(() => {
                                closeDialog();
                            });
                        }
                    })}>
                        <DeleteIcon color="primary"/>
                    </IconButton>
                </div>
            },
        }
    ];

    const transformData = (data?: OrderRecord) => {
        return data?.map((order: OrderRecord) => {
            const {shipName} = order.shipping || {};

            return {
                id: order.id,
                clientName: `${shipName}`,
                buyDate: order.buyDate,
                status: order.status,
                total: order.total,
                order: order,
            };
        });
    }

    const rows = transformData(filteredData);

    if (!filteredData || filteredData.length === 0) {
        return (
            <EmptyPage isLoading={isLoading} title={"Nessun ordine trovato"}
                       description={"Prova a cambiare i filtri o ad aggiungere nuovi ordini."}/>
        );
    }

    return (
        <div ref={gridRef}>
            <List {...props}>
                <DataGrid
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            background: 'white !important',
                        },
                        '& .MuiDataGrid-row--borderBottom': {
                            background: 'white !important',
                        },
                        '& .MuiToolbar-root': {
                            display: 'flex !important'
                        }
                    }}
                    pagination
                    paginationMode="client"
                    rows={rows}
                    columns={columns}
                    paginationModel={paginationModel}
                    initialState={{
                        sorting: {
                            sortModel: [{field: 'buyDate', sort: 'desc'}],
                        },
                    }}
                    onPaginationModelChange={setPaginationModel}
                />
            </List>
        </div>
    );
};

export const OrdersAdmin: React.FC = () => {
    const {isOpen: isOpenShow, onOpen: onOpenShow, onClose: onCloseShow} = useDisclosure();
    const [selectedOrder, setSelectedOrder] = useState<OrderRecord | undefined>(undefined);
    const {id, status} = selectedOrder || {};

    const {bg: bgColor, text: textColor, name} = getChipColor(status?.toUpperCase() || "") || {};

    return (
        <>
            <Admin dataProvider={customDataProvider} basename={"/orders"} i18nProvider={i18nProvider}
                   layout={CustomLayout} theme={theme}>
                <Resource name="shippings" edit={ShippingEdit}/>
                <Resource
                    name="orders"
                    hasCreate={false}
                    hasEdit={true}
                    hasShow={true}
                    list={(props) => OrderListCustom(props, onOpenShow, setSelectedOrder)}
                    show={OrderShow}
                />
            </Admin>
            <CustomModal isOpen={isOpenShow} hasSeparator onClose={() => {
                setSelectedOrder(undefined);
                onCloseShow();
            }} content={
                <ThemeProvider theme={themeGrid}>
                    <ShowOrderContent
                        orderId={selectedOrder?.id}/>
                </ThemeProvider>}
                         title={
                             <ThemeProvider theme={themeGrid}>
                                 <HStack>
                                     <Text fontSize={14} color={"black"}>#ID: {id}</Text>
                                     <Chip
                                         label={name}
                                         variant="outlined"
                                         sx={{
                                             fontWeight: 'bold',
                                             backgroundColor: bgColor,
                                             color: textColor
                                         }}
                                     />
                                 </HStack>
                             </ThemeProvider>}/>
        </>
    );
};

export const ShowOrderContent = ({orderId}: { orderId?: string }) => {
    const {getOrderWithId} = useOrdersHook();
    const [order, setOrder] = useState<OrderRecord | undefined>();

    const navigate = useNavigate();

    useEffect(() => {
        getOrderWithId(orderId || "").then(value => {
            setOrder(value);
        });
    }, []);

    const {id, items, shipping, total} = order || {};

    const {showDialog, setIsPending} = useDialogStore();

    const showConfirmDialog = useCallback((primaryClick: () => void) => showDialog({
        title: "Vuoi procedere?",
        description: "Vuoi modificare lo stato dell'ordine?<br>Premi <b>\"Annulla\"</b> per annullare; <b>\"Conferma\"</b> per procedere e avvisare il cliente tramite email",
        buttons: ({isPending, closeDialog}) => [
            <Button key={"cancel"} flex={1} onClick={closeDialog}>Annulla</Button>,
            <Button
                flex={1}
                key={"primary"}
                backgroundColor={"primary.400"}
                _hover={{
                    backgroundColor: "primary.500",
                    color: "white",
                }}
                _pressed={{
                    backgroundColor: "primary.700",
                    color: "white",
                }}
                color={"white"}
                onClick={async () => {
                    primaryClick();
                    closeDialog();
                }}>{isPending ? <Spinner/> : "Conferma"}</Button>
        ]
    }), []);

    const {
        shipAddress,
        shipCity,
        shipName,
        shipCountry,
        shipPhone,
        shipEmail,
        billingAddress,
        billingCity,
        billingName,
        billingCountry,
        billingPhone,
        invoiceRequired,
        piva
    } = shipping || {};

    const rows = items?.map((value) => {
        console.log({value});
        const {product: lot, id, quantity} = value || {};
        const {id: lotId, product, price, discountPrice, code, shippingFee} = lot || {};
        const {name, mediaList, id: productId} = product || {};
        const image = mediaList?.find(value => value.isMain)?.url;

        const finalPrice = (discountPrice || price || 0) * (quantity || 1);

        return {
            id,
            lotCode: code,
            lotId: lotId,
            name,
            quantity,
            singlePrice: discountPrice || price || 0,
            price: finalPrice,
            shippingFee,
            image,
            productId
        }
    });

    const {updateStatus, updateShipping, isLoading} = useOrdersHook();

    const {showModal, closeModal} = useModalStore();

    useEffect(() => {
        setIsPending(isLoading);
    }, [isLoading]);

    const columns = [
        {
            field: "image", headerName: "", innerWidth: 48, renderCell: (params: any) => (
                <HStack w={'100%'} alignItems={'center'} justifyContent={'center'}>
                    <Image src={params.row.image} width={"48px"} height={"48px"} objectFit={"contain"}/>
                </HStack>
            )
        },
        {
            field: "id", headerName: "ID", flex: 2, renderCell: (params: any) => {
                console.log({params});
                return (
                    <Tooltip title={params.row.id} arrow>
                        <Text display={"-webkit-box"}
                              overflow={"hidden"}
                              flex={1}
                              textOverflow={"ellipsis"}>{params.row.id}</Text>
                    </Tooltip>
                )
            }
        },
        {
            field: "name", headerName: "Prodotto", flex: 4, renderCell: (params: any) => (
                <Tooltip title={params.row.name} arrow>
                    <Box
                        onClick={() => {
                            navigate(`/products/edit/${params.row.productId}`);
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            whiteSpace: "normal",
                            cursor: 'pointer',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word",
                            }}
                        >
                            {params.row.name}
                        </Typography>
                    </Box>
                </Tooltip>)
        },
        {
            field: "lotCode",
            headerName: "Lotto",
            flex: 1,
            // renderCell: (params: any) => `${params.row.lotCode || params.row.lotId}`
            renderCell: (params: any) => (
                <Tooltip title={params.row.name} arrow>
                    <Box
                        onClick={() => {
                            navigate(`/products/lots/edit/${params.row.lotId}`);
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            whiteSpace: "normal",
                            cursor: 'pointer',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word",
                            }}
                        >
                            {`${params.row.lotCode || params.row.lotId}`}
                        </Typography>
                    </Box>
                </Tooltip>)
        },
        {
            field: "quantity", headerName: "Quantità", flex: 1
        },
        {
            field: "price",
            headerName: "Prezzo",
            flex: 1,
            renderCell: (params: any) => `${params.row.singlePrice?.toFixed(2)}€`
        },
        {
            field: "shippingFee",
            headerName: "Spedizione",
            flex: 1,
            renderCell: (params: any) => `${(params.value || 0)?.toFixed(2)}€`
        },
        {
            field: "totalPrice",
            headerName: "Totale",
            flex: 1,
            renderCell: (params: any) => `${((params.row.price || 0) + (params.row.shippingFee || 0))?.toFixed(2)}€`
        },
    ];

    return <VStack alignItems={"flex-start"} gap={0}>
        <HStack alignItems={'center'} mb={"16px"}>
            <Text fontSize={20} fontWeight={"bold"} color={ColorsClass.primaryGreen}>Informazioni
                spedizione</Text>
            <Container cursor={"pointer"} onClick={() => {
                showModal({
                    title: "Modifica spedizione",
                    content: <UpdateShippingForm initialData={shipping || {}} onSubmit={(data) => {
                        updateShipping(data).then(r => {
                            getOrderWithId(id || "").then(model => {
                                setOrder(model);
                                closeModal();
                            })
                        });
                    }}/>
                });
            }}><EditIcon/>
            </Container>
        </HStack>

        {invoiceRequired && <VStack alignItems={'flex-start'} gap={0} mb={'16px'}>
            <p style={{fontWeight: 'bold', color: '#deb818'}}>⚠️ Richiesta fattura</p>
            <p>Il cliente, con Partita IVA <b>${piva}</b>, ha richiesto la fattura per l'ordine.</p>
        </VStack>}

        <HStack w={'100%'} alignItems={'flex-start'}>
            <VStack flex={1} alignItems={'flex-start'} gap={0}>
                <HStack w={'100%'} alignItems={'stretch'}>
                    <VStack flex={1} alignItems={'flex-start'} gap={0}>
                        <Text fontSize={16} fontWeight={"bold"} color={ColorsClass.primaryGreen}>Indirizzo
                            spedizione</Text>
                        {shipName && <Text fontSize={14}>{shipName}</Text>}
                        {shipAddress && <Text fontSize={14}>{shipAddress}</Text>}
                        {shipCity && <Text fontSize={14}>{shipCity}</Text>}
                        {shipCountry && <Text fontSize={14}>{shipCountry}</Text>}
                        {shipPhone && <Text fontSize={14}>{shipPhone}</Text>}
                    </VStack>
                    <VStack flex={1} alignItems={'flex-start'} gap={0}>
                        <Text fontSize={16} fontWeight={"bold"} color={ColorsClass.primaryGreen}>Indirizzo di
                            fatturazione</Text>
                        {(billingName || billingAddress || billingCity || billingCountry || billingPhone) ? <>
                            {(billingName || shipName) && <Text fontSize={14}>{billingName || shipName}</Text>}
                            {(billingAddress || shipAddress) &&
                                <Text fontSize={14}>{billingAddress || shipAddress}</Text>}
                            {(billingCity || shipCity) && <Text fontSize={14}>{billingCity || shipCity}</Text>}
                            {(billingCountry || shipCountry) &&
                                <Text fontSize={14}>{billingCountry || shipCountry}</Text>}
                            {(billingPhone || shipPhone) && <Text fontSize={14}>{billingPhone || shipPhone}</Text>}
                        </> : <Text fontSize={14}>Uguale all'indirizzo di spedizione</Text>}
                    </VStack>
                </HStack>

                {shipEmail && <>
                    <Container h={"24px"}/>
                    <Text fontSize={16} fontWeight={"bold"} color={ColorsClass.primaryGreen}>Email</Text>
                    <Text as={"a"} href={`mailto:${shipEmail}`} textDecoration={"underline"} color={"#002d9e"}
                          onClick={(e) => {
                              e.preventDefault();
                              window.open(`mailto:${shipEmail}`, "_blank");
                          }} fontSize={14}>{shipEmail}</Text>
                </>}

                {total && <>
                    <Container h={"24px"}/>
                    <Text fontSize={16} fontWeight={"bold"} color={ColorsClass.primaryGreen}>Totale</Text>
                    <Text fontSize={18} fontWeight={'bold'}>{total?.toFixed(2)}€</Text>
                </>}
            </VStack>
            <VStack flex={1} maxW={300} alignItems={'flex-start'}>
                <Text>Modifica lo stato dell'ordine:</Text>
                <Tooltip title={"Ordine confermato, non ancora in fase di elaborazione."} arrow enterDelay={500}
                         enterNextDelay={500}>
                    <Button backgroundColor={"#e4e4e4"} onClick={() => showConfirmDialog(() => {
                        id && updateStatus({
                            orderId: id,
                            orderStatus: "pending"
                        }).then(() => window.location.reload());
                    })} color={"#002d9e"} fontWeight={'semibold'} py={"8px"}
                            px={"16px"} borderRadius={4}
                            alignSelf={'stretch'}>Conferma</Button>
                </Tooltip>
                <Tooltip title={"Ordine temporaneamente bloccato."} arrow enterDelay={500} enterNextDelay={500}>
                    <Button backgroundColor={"#e4e4e4"} onClick={() => showConfirmDialog(() => {
                        id && updateStatus({
                            orderId: id,
                            orderStatus: "on_hold"
                        }).then(() => window.location.reload());
                    })} color={"#5a1658"} fontWeight={'semibold'} py={"8px"} px={"16px"} borderRadius={4}
                            alignSelf={'stretch'}>Sospendi</Button>
                </Tooltip>
                <Tooltip title={"In lavorazione: preparazione, imballaggio, ecc."} arrow enterDelay={500}
                         enterNextDelay={500}>
                    <Button backgroundColor={"#e4e4e4"} color={"#b07d00"} py={"8px"} px={"16px"}
                            onClick={() => showConfirmDialog(() => {
                                id && updateStatus({
                                    orderId: id,
                                    orderStatus: "processing"
                                }).then(() => window.location.reload());
                            })} borderRadius={4} fontWeight={'semibold'}
                            alignSelf={'stretch'}>In lavorazione</Button>
                </Tooltip>
                <Tooltip title={"Concluse le operazioni di preparazione, è stato spedito al destinatario."} arrow
                         enterDelay={500} enterNextDelay={500}>
                    <Button backgroundColor={"#e4e4e4"} onClick={() => showConfirmDialog(() => {
                        id && updateStatus({
                            orderId: id,
                            orderStatus: "shipped"
                        }).then(() => window.location.reload());
                    })} color={"#256029"} py={"8px"} fontWeight={'semibold'} px={"16px"} borderRadius={4}
                            alignSelf={'stretch'}>Spedito</Button>
                </Tooltip>
                <Tooltip title={"Concluse le operazioni di preparazione, è stato spedito al destinatario."} arrow
                         enterDelay={500} enterNextDelay={500}>
                    <Button backgroundColor={"#e4e4e4"} onClick={() => showConfirmDialog(() => {
                        id && updateStatus({
                            orderId: id,
                            orderStatus: "completed"
                        }).then(() => window.location.reload());
                    })} color={"#00930e"} py={"8px"} fontWeight={'semibold'} px={"16px"} borderRadius={4}
                            alignSelf={'stretch'}>Completato</Button>
                </Tooltip>
            </VStack>
        </HStack>

        <Container h={"48px"}/>

        <HStack flex={1} alignSelf={'stretch'}>
            <DataGrid columns={columns} autoPageSize onRowClick={(params: any) => {

            }} sx={{
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'white',
                },
            }} rows={rows} style={{
                width: "100%",
                alignSelf: 'center',
            }}/>
        </HStack>
    </VStack>
}
